import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { structureApi, type PositionNode } from "../api/structure";
import type { MountState } from "../api/assets";
import type { ApiError } from "../api/client";
import { usePositionKinds } from "../lib/usePositionKinds";
import { flattenForest, loadWholeForest, type FlatPosition } from "../lib/positionTree";
import { matchesQuery } from "../lib/foldText";
import { ALL_ROWS, TableFooter, readPageSize, storePageSize, type PageSize } from "./TableFooter";
import { Select, type SelectOption } from "./Select";
import { PositionKindIcon, PositionKindName } from "./PositionKindIcon";
import Ic from "./icons.jsx";

const ALL = "";
const PAGE_SIZE_KEY = "bkm.positions.pageSize";
/** `?mountState=EMPTY` (the dashboard "Prazne pozicije" tile) pre-selects the state filter. */
export const MOUNT_STATE_PARAM = "mountState";
const MOUNT_STATES: MountState[] = ["EMPTY", "MOUNTED", "TEMPORARY", "NONE"];

function mountStateFromUrl(value: string | null): MountState | typeof ALL {
  return (MOUNT_STATES as string[]).includes(value ?? "") ? (value as MountState) : ALL;
}
const INDENT_PX = 22;

/**
 * The structure as a table, in the SAME order as the tree (depth-first, siblings by
 * sortOrder / sequenceNo / name — Strahinja, 2026-09-11): the whole forest is loaded once
 * (`loadWholeForest`), flattened, and filtered client-side by code / name, kind and mount state. Rows are
 * indented by depth; a row click hands the position to the caller (who opens its card).
 */
export function PositionsTable({ onOpen }: { onOpen: (position: { id: string }) => void }) {
  const { t } = useTranslation();
  const { kinds } = usePositionKinds();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [kind, setKind] = useState<string>(ALL);
  const [searchParams] = useSearchParams();
  const [mountState, setMountState] = useState<MountState | typeof ALL>(() => mountStateFromUrl(searchParams.get(MOUNT_STATE_PARAM)));
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSize>(() => readPageSize(PAGE_SIZE_KEY, 50));
  const [forest, setForest] = useState<PositionNode[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);

  // Any filter change restarts at the first page.
  useEffect(() => { setPage(0); }, [debouncedQ, kind, mountState, pageSize]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    loadWholeForest(structureApi)
      .then((f) => { if (!cancelled) setForest(f); })
      .catch((err: ApiError) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, []);

  const kindOptions = useMemo<SelectOption<string>[]>(
    () => [{ value: ALL, label: t("structure.table.allKinds", "Sve vrste") }, ...kinds.map((k) => ({ value: k.code, label: k.name, left: <PositionKindIcon kind={k.code} /> }))],
    [kinds, t],
  );

  const mountStateOptions = useMemo<SelectOption<MountState | typeof ALL>[]>(
    () => [
      { value: ALL, label: t("structure.table.allStates", "Sva stanja") },
      { value: "EMPTY", label: t("structure.table.state.empty", "Prazne"), left: <span className="tree-dot is-empty" aria-hidden="true" /> },
      { value: "MOUNTED", label: t("structure.table.state.mounted", "Montiran asset"), left: <span className="tree-dot is-mounted" aria-hidden="true" /> },
      { value: "TEMPORARY", label: t("structure.table.state.temporary", "Privremena zamena"), left: <span className="tree-dot is-temporary" aria-hidden="true" /> },
      { value: "NONE", label: t("structure.table.state.none", "Strukturne (bez asseta)") },
    ],
    [t],
  );

  const all = useMemo(() => (forest ? flattenForest(forest) : []), [forest]);
  const rows = useMemo(
    () => all.filter((r) => (kind === ALL || r.node.kind === kind)
      && (mountState === ALL || r.node.mountState === mountState)
      && (!debouncedQ || matchesQuery(`${r.node.code ?? ""} ${r.node.name}`, debouncedQ))),
    [all, kind, mountState, debouncedQ],
  );
  const filtered = kind !== ALL || mountState !== ALL || debouncedQ !== "";
  const pageRows = pageSize === ALL_ROWS ? rows : rows.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <>
      <div className="filter-bar">
        <div className="search-wrap">
          <Ic.search aria-hidden="true" />
          <input
            className="input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("structure.table.searchPlaceholder", "Šifra ili naziv pozicije…")}
            aria-label={t("action.search", "Pretraga")}
          />
        </div>
        <div style={{ flex: "0 1 220px", minWidth: 180 }}>
          <Select<string> value={kind} options={kindOptions} onChange={setKind} ariaLabel={t("structure.col.kind", "Vrsta")} />
        </div>
        <div style={{ flex: "0 1 220px", minWidth: 180 }}>
          <Select<MountState | typeof ALL> value={mountState} options={mountStateOptions} onChange={setMountState} ariaLabel={t("structure.table.stateLabel", "Stanje")} />
        </div>
        {forest && (
          <span className="muted" style={{ fontSize: 12.5, marginLeft: "auto" }}>
            {filtered
              ? t("structure.table.countFiltered", "{{count}} od {{total}} pozicija", { count: rows.length, total: all.length })
              : t("structure.table.count", "{{count}} pozicija", { count: all.length })}
          </span>
        )}
      </div>

      <section className="card">
        {error ? (
          <div style={errorBox} role="alert">{error}</div>
        ) : forest === null ? (
          <div className="empty">{t("common.loading", "Učitavanje…")}</div>
        ) : rows.length === 0 ? (
          <div className="empty">{t("structure.table.empty", "Nema pozicija za zadate filtere.")}</div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl list-card compact">
              <thead>
                <tr>
                  <th>{t("structure.col.name", "Naziv")}</th>
                  <th>{t("structure.col.code", "Šifra")}</th>
                  <th>{t("structure.col.kind", "Vrsta")}</th>
                  <th>{t("structure.field.path", "Putanja")}</th>
                  <th>{t("structure.col.expectedType", "Očekivani tip")}</th>
                  <th>{t("structure.col.mountedAsset", "Montiran asset")}</th>
                  <th className="num">{t("structure.col.children", "Pod-pozicije")}</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => <Row key={r.node.id} row={r} indent={!filtered} onOpen={onOpen} />)}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <TableFooter
        page={page}
        pageSize={pageSize}
        total={rows.length}
        onPage={setPage}
        onPageSize={(s) => { storePageSize(PAGE_SIZE_KEY, s); setPageSize(s); }}
      />
    </>
  );
}

function Row({ row, indent, onOpen }: { row: FlatPosition; indent: boolean; onOpen: (p: { id: string }) => void }) {
  const { t } = useTranslation();
  const p = row.node;
  return (
    <tr className="is-clickable" onClick={() => onOpen(p)}>
      <td data-label={t("structure.col.name", "Naziv")}>
        <span
          className="positions-table-name"
          style={{ paddingLeft: indent ? row.depth * INDENT_PX : 0 }}
        >
          {indent && row.depth > 0 && <span className="positions-table-guide" aria-hidden="true" />}
          <span className="tree-icon"><PositionKindIcon kind={p.kind} /></span>
          <b>{p.name}</b>
          {p.sequenceNo !== null && <span className="muted">#{p.sequenceNo}</span>}
        </span>
      </td>
      <td className="mono" data-label={t("structure.col.code", "Šifra")}>{p.code ?? <span className="muted">—</span>}</td>
      <td data-label={t("structure.col.kind", "Vrsta")}><PositionKindName kind={p.kind} /></td>
      <td data-label={t("structure.field.path", "Putanja")} className="muted" style={{ fontSize: 12.5 }}>
        {row.path.length > 0 ? row.path.map((e) => e.name).join(" / ") : t("structure.table.topLevel", "prvi nivo")}
      </td>
      <td data-label={t("structure.col.expectedType", "Očekivani tip")} style={{ whiteSpace: "nowrap" }}>{p.expectedTypeName ?? <span className="muted">—</span>}</td>
      <td data-label={t("structure.col.mountedAsset", "Montiran asset")}>
        {p.mountedAssetId ? (
          <span className={`tag ${p.mountState === "TEMPORARY" ? "warn" : "ok"} dot`}>
            <Link to={`/asseti/${p.mountedAssetId}`} className="mono" style={{ color: "inherit" }} onClick={(e) => e.stopPropagation()}>{p.mountedAssetTag}</Link>
          </span>
        ) : p.mountState === "EMPTY" ? (
          <span className="tag err dot">{t("structure.emptySlotShort", "Prazna")}</span>
        ) : <span className="muted">—</span>}
      </td>
      <td className="num" data-label={t("structure.col.children", "Pod-pozicije")}>{p.childCount}</td>
    </tr>
  );
}

const errorBox: CSSProperties = { padding: 12, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
